import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { ConnectionPositionPair, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  InputSignal,
  OnDestroy,
  PLATFORM_ID,
  Signal,
  ViewChild,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { isSameDay } from 'date-fns';
import { Subject } from 'rxjs';
import { ToggleComponent } from 'src/app/shared/toggle/toggle.component';
import {
  AmChartLine,
  AmLineChartItem,
  FillSectionGeneration,
  TimelineChart,
  TimelineChartInputV2,
  TimelineChartLegendItem,
  TimelineChartSection,
  TimelineChartSectionTooltip,
  TimelineChartToggle,
  isAmChartLegendItem,
  isTimelineLegendItem,
} from '../../models/timeline-chart-input.interface';
import { TimelineCursorPosition } from '../../models/timeline-cursor-position.interface';
import { IntervalHHMMPipe } from '../../pipes/interval-hhmm.pipe';
import { AmChartService } from '../../service/am-chart.service';
import { TimelineChartService } from '../../service/timeline-chart.service';
import { TimelineChartLegendComponent } from '../timeline-chart-legend/timeline-chart-legend.component';

@Component({
  selector: 'pozi-timeline-chart-v2',
  imports: [
    CommonModule,
    RouterLink,
    OverlayModule,
    ToggleComponent,
    TimelineChartLegendComponent,
    IntervalHHMMPipe,
  ],
  templateUrl: './timeline-chart-v2.component.html',
  styleUrl: './timeline-chart-v2.component.scss',
})
export class TimelineChartV2Component implements OnDestroy, AfterViewInit {
  private platformId = inject(PLATFORM_ID);
  timelineChartService = inject(TimelineChartService);
  private amChartService = inject(AmChartService);

  /** Gaps and layout sizes */
  readonly MAIN_CONTAINER_GAP = 16;
  readonly LEGEND_HEIGHT = 24;

  readonly TOOLTIP_CONTAINER_GAP = 8;
  readonly TOOLTIP_ROW_HEIGHT = 24;

  readonly AM_CHART_HEIGHT = 64;
  readonly AM_CHART_HEIGHT_L = 88;

  /** Width reserved for the Y-axis column to the right of the AmChart */
  readonly Y_AXIS_WIDTH = 40;

  /** Increment the Y-axis top label is rounded up to */
  readonly Y_AXIS_STEP = 50;

  /** Y-axis top label used when the AmChart has no positive values */
  readonly DEFAULT_Y_AXIS_TOP = 100;

  /** Timeline margin options */
  readonly TIMELINE_MARGIN_SMALL = 8;
  readonly TIMELINE_MARGIN_LARGE = 16;

  /** Timeline gap defaults */
  readonly DEFAULT_TIMELINE_GAP = 4;
  readonly DEFAULT_TIMELINE_GAP_TO_FILL = 60_000; // 1 minute in milliseconds

  /** Default styling for timeline sections */
  readonly DEFAULT_SECTION_HEIGHT = 8;
  readonly DEFAULT_SECTION_COLOR = '#B4C8DC';
  readonly DEFAULT_MINIMUM_SECTION_WIDTH = 4; // in px

  /** Special generated section identifiers */
  readonly GENERATED_SECTION_KEY = 'generated-section';
  readonly GENERATED_SECTION_COLOR = '#E8EEF4';

  readonly TIME_LABEL_HEIGHT = 16;

  /** Template references for DOM elements */
  @ViewChild('mainContainer', { static: true })
  mainContainerRef!: ElementRef;

  @ViewChild('timelineContainer')
  timelineContainerRef!: ElementRef;

  timelineWidth = signal<number>(0);

  @ViewChild('amChartContainer')
  amChartContainerRef!: ElementRef;

  yAxis = input<boolean>(false);

  /** Input data signal for timeline charts */
  data: InputSignal<TimelineChartInputV2> =
    input.required<TimelineChartInputV2>();

  /**
   * Computed signal that processes the raw timeline input data.
   *
   * Processing includes:
   *  - Attaching a unique identifier (UUID) to each section.
   *  - Removing sections that do not meet the minimum width requirement.
   *
   */
  processedData = computed(() => {
    const data: TimelineChartInputV2 = this.data();

    if (!data.timelines) return data;

    return {
      ...data,
      timelines: data.timelines.map((timeline: TimelineChart) => {
        let processedTimeline: TimelineChart = { ...timeline };

        // Attaches UUIDs to sections
        processedTimeline = {
          ...processedTimeline,
          timelineSections: processedTimeline.timelineSections.map(
            (section: TimelineChartSection) => this.attachUUID(section)
          ),
        };

        // Removes sections too small width
        processedTimeline = this.removeSectionUnderMinWidth(
          processedTimeline,
          this.DEFAULT_MINIMUM_SECTION_WIDTH,
          this.timelineWidth()
        );

        return processedTimeline;
      }),
    };
  });

  showHideToggle = input<boolean>(false);

  /**
   * Processes all timelines by:
   *  1. Validating their sections.
   *  2. Clamp the first and last sections, if global startTime and endTime are defined.
   *  3. Filling in any time gaps between sections.
   *  4. Converting section durations into percentage ratios of the total.
   *
   * Returns a new array of processed `TimelineChart` objects.
   */
  timelines: Signal<TimelineChart[]> = computed(() => {
    const data = this.processedData();

    if (!data?.timelines) {
      if (!data.amChart) {
        console.error('No timeline data found in `this.data()`');
      }
      return [];
    }

    return data.timelines.map((timelineChart: TimelineChart) => {
      // Step 1: Validate
      this.validateSection(timelineChart.timelineSections);

      let processedTimeline: TimelineChart = { ...timelineChart };

      if (timelineChart.timelineSections.length === 0) {
        return timelineChart;
      }

      // Step 2: Clamp to global range
      const globalStart = data.startTime ? new Date(data.startTime) : null;
      const globalEnd = data.endTime ? new Date(data.endTime) : null;
      processedTimeline = this.clampTimelineSectionsToGlobalRange(
        processedTimeline,
        globalStart,
        globalEnd
      );

      processedTimeline = this.removeSectionUnderMinWidth(
        processedTimeline,
        this.DEFAULT_MINIMUM_SECTION_WIDTH,
        this.timelineWidth()
      );

      // Step 3: Fill gaps
      processedTimeline = {
        ...processedTimeline,
        timelineSections: this.fillTimelineGaps(
          timelineChart.timelineSections,
          timelineChart.generateFillSections,
          this.startTime()!,
          this.endTime()!
        ),
      };

      // Step 4: Convert durations to ratio
      processedTimeline =
        this.convertTimelineIntervalToRatio(processedTimeline);

      return processedTimeline;
    });
  });

  /**
   * A computed signal that filters out timelines whose `key` is present
   * in `hiddenTimelineKeys`.
   *
   * Only timelines without a `key`, or whose `key` is not hidden, will be returned.
   *
   * @returns {TimelineChart[]} An array of visible timeline charts.
   */
  displayedTimelines: Signal<TimelineChart[]> = computed(() => {
    return this.timelines().filter(
      (timeline) =>
        !timeline.key || !this.hiddenTimelineKeys().includes(timeline.key)
    );
  });

  isTimelinePresent = computed(
    () => this.data().timelines && this.data().timelines!.length > 0
  );

  containerClass = computed(() => {
    if (this.isAmChartDisplayed()) {
      return this.isTimelinePresent()
        ? 'container-with-am-chart'
        : 'container-am-chart-only';
    } else {
      return 'container-without-am-chart';
    }
  });

  amChartHeight = computed(() => {
    return this.isTimelinePresent()
      ? this.AM_CHART_HEIGHT
      : this.AM_CHART_HEIGHT_L;
  });

  legendKeyColorMapInput = input<Map<string, string | undefined>>();

  /**
   * A computed signal that generates a mapping between legend item section keys and their corresponding colors.
   *
   * The map is derived from `data().legendItems`, where each item's `sectionKey` becomes the map key
   * and `color` becomes the value.
   *
   * @returns {Map<string, string>} A map of section keys to color values.
   */
  legendKeyColorMap = computed(() => {
    return (
      this.legendKeyColorMapInput() ??
      this.timelineChartService.getLegendKeyColorMap(this.data().legendItems)
    );
  });

  /**
   * A signal which stores the currently highlighted TimelineChartLegendItem, which is hovered over by the mouse.
   */
  highlightedLegendItem = signal<TimelineChartLegendItem | null>(null);

  /**
   * Computes the earliest start time across all timelines.
   * If AmChart is present, the first element of its data is also compared.
   *
   * Iterates through `this.data().timelines` (if present) to find the first section's `startTime` in each timeline,
   * filters out missing or invalid values, and returns the earliest as a `Date` object.
   *
   * @returns {Date | null} The earliest start time, or `null` if no valid dates are found.
   */
  startTime: Signal<Date | null> = computed(() => {
    if (this.data().startTime) {
      return new Date(this.data().startTime!);
    }

    const timelineFirstDates =
      this.data()
        .timelines?.map((t) => t.timelineSections?.[0]?.startTime)
        .filter((date): date is string => Boolean(date)) ?? [];

    const amChartFirstDate = this.data()?.amChart?.data?.[0]?.date;

    const allFirstDates = amChartFirstDate
      ? [...timelineFirstDates, amChartFirstDate]
      : timelineFirstDates;

    if (allFirstDates.length === 0) {
      return null;
    }

    const earliestDate = allFirstDates.reduce((earliest, current) =>
      new Date(current) < new Date(earliest) ? current : earliest
    );

    return new Date(earliestDate);
  });

  /**
   * Computes the latest end time across all timelines.
   * If AmChart is present, the last element of its data is also compared.
   *
   * Iterates through `this.data().timelines` (if present) to find the last section's `endTime` in each timeline,
   * filters out missing or invalid values, and returns the latest as a `Date` object.
   *
   * @returns {Date | null} The latest end time, or `null` if no valid dates are found.
   */
  endTime: Signal<Date | null> = computed(() => {
    if (this.data().endTime) {
      return new Date(this.data().endTime!);
    }

    const timelineLastDates =
      this.data()
        .timelines?.map((t) => t.timelineSections?.at(-1)?.endTime)
        .filter((date): date is string => Boolean(date)) ?? [];

    const amChartLastDate = this.data()?.amChart?.data?.at(-1)?.date;

    const allLastDates = amChartLastDate
      ? [...timelineLastDates, amChartLastDate]
      : timelineLastDates;

    if (allLastDates.length === 0) {
      return null;
    }

    const latestDate = allLastDates.reduce((latest, current) =>
      new Date(current) > new Date(latest) ? current : latest
    );

    return new Date(latestDate);
  });

  /**
   * Retrieves the earliest section `startTime` across all timelines of a given TimelineChartInputV2.
   *
   * This function looks at every `timelineSection` in each `TimelineChart` contained
   * within the `TimelineChartInputV2`. It collects all defined `startTime` values
   * and returns the earliest one.
   *
   * @param chart - The timeline chart input object to extract the first section startTime from.
   *
   * @returns The earliest `startTime` found as a Date, or `null` if no section
   *          has a defined `startTime`.
   */
  firstSectionStartTime = computed(() => {
    const timelines = this.timelines();

    if (!timelines || timelines.length === 0) {
      return null;
    }

    return this.timelineChartService.getFirstSectionStartTime(timelines);
  });

  /**
   * Retrieves the latest section `endTime` across all timelines of a given TimelineChartInputV2.
   *
   * This function looks at every `timelineSection` in each `TimelineChart` contained
   * within the `TimelineChartInputV2`. It collects all defined `endTime` values
   * and returns the latest one.
   *
   * @param chart - The timeline chart input object to extract the last section endTime from.
   *
   * @returns The latest `endTime` found as a Date, or `null` if no section
   *          has a defined `endTime`.
   */
  lastSectionEndTime = computed(() => {
    const timelines = this.timelines();
    if (!timelines || timelines.length === 0) {
      return null;
    }

    return this.timelineChartService.getLastSectionEndTime(timelines);
  });

  timelinePercentage = computed(() => {
    const startTime = this.startTime()!.getTime();
    const endTime = this.endTime()!.getTime();
    const firstSectionStartTime = this.firstSectionStartTime()!.getTime();
    const lastSectionEndTime = this.lastSectionEndTime()!.getTime();

    // Calculate overlap
    const overlapStart = Math.max(startTime, firstSectionStartTime);
    const overlapEnd = Math.min(endTime, lastSectionEndTime);

    if (overlapEnd <= overlapStart) {
      return 0; // no overlap
    }

    const overlapDuration = overlapEnd - overlapStart;
    const outerDuration = endTime - startTime;

    return (overlapDuration / outerDuration) * 100;
  });

  /**
   * Computes the overall interval of the TimelineChart in ms.
   */
  sumIntervalInMs = computed(() => {
    if (!this.startTime() || !this.endTime()) {
      return null;
    }
    return this.timelineChartService.getDuration(
      this.startTime()!,
      this.endTime()!
    );
  });

  highlightedLegendItemInput = input<TimelineChartLegendItem | null>();

  /**
   * Signal that stores the current cursor position relative to the timeline.
   *
   * It holds an object with:
   * - `offsetX`: The cursor's horizontal offset in pixels from the left edge of the timeline container.
   * - `timelineWidth`: The width in pixels of the timeline container.
   * - `percentage`: The cursor's horizontal position expressed as a percentage (0 to 100) of the timeline width.
   *
   * This signal is `null` when no cursor position is available.
   */
  cursorPositionOnTimeline = signal<TimelineCursorPosition | null>(null);

  /**
   * Computes the exact date/time corresponding to the current cursor position on the timeline.
   *
   * Calculates the date by interpolating between the overall `startTime` and `endTime`
   * based on the cursor's horizontal percentage position on the timeline.
   *
   * Returns `null` if `startTime`, `endTime`, or the cursor position is not available.
   *
   * @returns {Date | null} The interpolated date under the cursor, or `null` if unavailable.
   */
  dateOnCursor = computed(() => {
    if (
      !this.startTime() ||
      !this.endTime() ||
      !this.cursorPositionOnTimeline()
    ) {
      return null;
    }

    const start = this.startTime()!.getTime();
    const end = this.endTime()!.getTime();
    const position = this.cursorPositionOnTimeline()!.percentage / 100;

    return new Date(start + (end - start) * position);
  });

  /**
   * Computed signal that determines the {@link AmLineChartItem} closest
   * to the current cursor position on the chart.
   *
   * It reacts to changes in:
   * - `this.data().amChart` (chart input data)
   * - `this.dateOnCursor()` (date corresponding to cursor position)
   *
   * @remarks
   * If either the chart input (`amChartInput`) or the cursor date (`targetDate`)
   * is missing, the computed value will be `null`.
   *
   * @returns The {@link AmLineChartItem} closest to the cursor's date, or `null` if
   *          there is no chart data or cursor date available.
   */
  closestAmChartItemToCursor = computed(() => {
    const amChartInput = this.data().amChart;
    const targetDate = this.dateOnCursor();

    if (
      !amChartInput ||
      !targetDate ||
      this.isOnAnyOfDays(targetDate, this.daysWithoutValue())
    ) {
      return null;
    }

    return this.amChartService.getClosestAmChartItemByDate(
      targetDate,
      amChartInput
    );
  });

  /**
   * Checks if a target date matches the same calendar day as any date in a list.
   * Hours, minutes, seconds, and milliseconds are ignored; only year, month, and day are considered.
   *
   * @param targetDate - The date to check.
   * @param datesToCheck - An array of dates to compare against.
   * @returns `true` if the target date is on the same day as any date in `datesToCheck`, otherwise `false`.
   */
  isOnAnyOfDays(targetDate: Date, datesToCheck: Date[]): boolean {
    return datesToCheck.some((date) => isSameDay(targetDate, date));
  }

  /**
   * Computes the list of timeline sections currently hovered by the cursor.
   *
   * Determines which sections contain the date represented by the current cursor position (`dateOnCursor`).
   * Returns an array of all matching `TimelineChartSection`s across all displayed timelines.
   * If no valid cursor date is available, returns an empty array.
   *
   * @returns {TimelineChartSection[]} An array of timeline sections under the cursor position.
   */
  hoveredSections = computed<TimelineChartSection[]>(() => {
    const cursorDate = this.dateOnCursor();
    if (!cursorDate) return [];

    const targetTime = cursorDate.getTime();

    return this.displayedTimelines().flatMap((timeline: TimelineChart) =>
      timeline.timelineSections.filter((section) => {
        if (!section.startTime || !section.endTime) return false;

        const start = new Date(section.startTime).getTime();
        const end = new Date(section.endTime).getTime();

        return start <= targetTime && targetTime <= end;
      })
    );
  });

  /**
   * Computes the tooltips currently displayed on the timeline.
   *
   * Combines AmChart tooltips near the cursor and section tooltips of hovered sections.
   * If there is no closest AmChart item, only section tooltips are returned.
   *
   * @returns {TimelineChartSectionTooltip[]} Array of tooltips to display.
   */
  displayedTooltips = computed<TimelineChartSectionTooltip[]>(() => {
    const amChartTooltips = this.amChartTooltips();
    const sectionTooltips = this.displayedSectionTooltips();

    return this.closestAmChartItemToCursor()
      ? [...amChartTooltips, ...sectionTooltips]
      : sectionTooltips;
  });

  /**
   * Computes tooltips based on the AmChart data item closest to the cursor.
   *
   * Maps the closest AmChart item's values to tooltips, filtering out hidden timeline keys,
   * and attaching icon info (symbol, color) from the chart's line configuration.
   *
   * @returns {TimelineChartSectionTooltip[]} Array of tooltips derived from AmChart data.
   */
  amChartTooltips = computed<TimelineChartSectionTooltip[]>(() => {
    const closestItem = this.closestAmChartItemToCursor();
    if (!closestItem) return [];

    const amChartValues = Object.entries(closestItem.values).map(
      ([key, value]) => ({ key, value })
    );

    return amChartValues
      .filter(({ key }) => !this.hiddenTimelineKeys().includes(key))
      .map(({ key, value }) => {
        const line = this.data().amChart?.lines.find(
          (line: AmChartLine) => line.key === key
        );
        const additionalTooltipValues = closestItem.tooltip?.[key] || [];

        return {
          icon: {
            symbol: line?.symbol ?? 'line',
            color: line?.color,
          },
          values: [
            `<strong>${value}${line?.unit === '%' ? '' : ' '}${line?.unit ?? ''}</strong>`,
            ...additionalTooltipValues,
          ],
        } as TimelineChartSectionTooltip;
      });
  });

  /**
   * Computes tooltips for timeline sections currently hovered by the cursor.
   *
   * Returns tooltips for hovered sections, ensuring each tooltip's icon color is set.
   * If a tooltip's icon color is missing, it is filled from the legend's color map using the section key.
   *
   * @returns {TimelineChartSectionTooltip[]} Array of tooltips for hovered timeline sections.
   */
  displayedSectionTooltips = computed<TimelineChartSectionTooltip[]>(() => {
    return this.hoveredSections()
      .filter((section) => !!section.tooltip)
      .map((section) => {
        const tooltip = section.tooltip as TimelineChartSectionTooltip;

        if (tooltip.icon.color) {
          return tooltip;
        }

        const colorFromLegend = this.legendKeyColorMap().get(section.key ?? '');

        return {
          ...tooltip,
          icon: {
            ...tooltip.icon,
            color: colorFromLegend,
          },
        };
      });
  });

  /**
   * Computes the total height of the tooltip container based on
   * the number of displayed tooltips and configured row height and gaps.
   *
   * @returns {number} The height in pixels required to display all tooltips with gaps.
   */
  tooltipContainerHeight = computed<number>(() => {
    const tooltipsCount = this.displayedTooltips().length;

    return (
      tooltipsCount * (this.TOOLTIP_ROW_HEIGHT + this.TOOLTIP_CONTAINER_GAP) +
      this.TOOLTIP_CONTAINER_GAP
    );
  });

  /**
   * Computes the total height of the tooltip seeker element,
   * which includes the tooltip container height and its aura height.
   *
   * Returns 0 if cursor position on the timeline is not available.
   *
   * @returns {number} The combined height of the tooltip seeker or 0 if unavailable.
   */
  tooltipSeekerHeight = computed<number>(() => {
    if (!this.cursorPositionOnTimeline()) return 0;
    return this.tooltipContainerHeight() + this.tooltipSeekerAuraHeight();
  });

  /**
   * Computes the height of the tooltip seeker's aura.
   *
   * This is based on the timeline container's height plus additional
   * heights for the AmChart (if displayed), main container gaps, and time label height.
   * Returns 0 if cursor position on the timeline is not available.
   *
   * @returns {number} The height of the tooltip seeker aura or 0 if unavailable.
   */
  tooltipSeekerAuraHeight = computed<number>(() => {
    if (!this.cursorPositionOnTimeline()) return 0;

    const timelineHeight =
      this.timelineContainerRef?.nativeElement.getBoundingClientRect().height ??
      0;

    const amChartHeight = this.isAmChartDisplayed()
      ? this.amChartHeight() + this.MAIN_CONTAINER_GAP
      : 0;

    return (
      timelineHeight +
      amChartHeight +
      this.MAIN_CONTAINER_GAP * 2 +
      this.TIME_LABEL_HEIGHT
    );
  });

  /**
   * Computes an array of static time labels for the timeline.
   *
   * Includes the start time, a series of hourly dates between start and end,
   * and the end time itself.
   *
   * Assumes `startTime` and `endTime` are non-null.
   *
   * @returns {Date[]} Array of Date objects representing fixed timeline labels.
   */
  staticTimeLabels = computed<Date[]>(() => {
    const start = this.startTime();
    const end = this.endTime();
    if (!start || !end) return [];

    return this.areDayLabelsDisplayed()
      ? this.getDatesAt(start, end, 9)
      : this.timelineChartService.getStaticTimeLabels(
          start,
          end,
          this.timelineWidth()
        );
  });

  areDayLabelsDisplayed = computed<boolean>(() => {
    if (!this.startTime() || !this.endTime()) return false;
    return this.isIntervalMoreThanTwoDays(this.startTime()!, this.endTime()!);
  });

  daysWithoutValue = computed(() => {
    return this.staticTimeLabels().filter((date: Date) =>
      this.isDayWithoutValue(date)
    );
  });

  /**
   * Computes the time labels for display on the timeline.
   *
   * Includes all static time labels plus the current cursor date if available.
   *
   * @returns {Date[]} Array of Date objects including static labels and optionally the hovered date.
   */
  timeLabels = computed<Date[]>(() => {
    const hoveredDate = this.dateOnCursor();
    return hoveredDate
      ? [...this.staticTimeLabels(), hoveredDate]
      : this.staticTimeLabels();
  });

  /**
   * Computes the dynamic style object for positioning and styling the tooltip container
   * relative to the cursor position on the timeline.
   *
   * Positions the tooltip above the cursor, adjusting horizontally to avoid overflow:
   * - If cursor is on the left half (< 50%), tooltip is placed to the right of cursor.
   * - Otherwise, tooltip is placed to the left of cursor.
   *
   * Applies appropriate border-radius to visually connect the tooltip to the cursor side.
   *
   * @returns CSS styles for the tooltip container.
   */
  tooltipContainerStyle = computed(() => {
    const cursor = this.cursorPositionOnTimeline()!;

    const bottom = `${this.tooltipSeekerHeight() - this.tooltipContainerHeight() + 8}px`;

    if (cursor.percentage < 50) {
      return {
        bottom,
        left: `${cursor.offsetX + 2}px`, // 2px is the width of the seeker
        'border-top-right-radius': '8px',
        'border-bottom-right-radius': '8px',
      };
    } else {
      return {
        bottom,
        right: `${cursor.timelineWidth - cursor.offsetX}px`,
        'border-top-left-radius': '8px',
        'border-bottom-left-radius': '8px',
      };
    }
  });

  /**
   * Keys of the Timelines, that are not currently displayed.
   */
  hiddenTimelineKeys = signal<string[]>([]);

  /**
   * Keys of the AmChart Series, that are not currently displayed.
   */
  hiddenAmChartSeriesKeys = signal<string[]>([]);

  /**
   * Determines whether the AmChart should be displayed.
   *
   * Returns false if no amChart data is present or if all series keys
   * are currently hidden according to `hiddenAmChartSeriesKeys`.
   *
   * @returns {boolean} True if AmChart is present and has at least one visible series; otherwise false.
   */
  isAmChartDisplayed = computed(() => {
    if (!this.data().amChart) return false;

    const seriesKeys = Array.from(this.amChartSeries().keys());
    const allHidden = seriesKeys.every((key) =>
      this.hiddenAmChartSeriesKeys().includes(key)
    );

    return !allHidden;
  });

  /**
   * The highest value plotted on the AmChart, across every series declared in
   * `lines` — hidden series included, so the scale stays stable while the user
   * toggles series on and off.
   *
   * @returns {number} The maximum plotted value, or 0 when there is no AmChart data.
   */
  amChartMaxValue = computed<number>(() => {
    const amChart = this.data().amChart;
    if (!amChart) return 0;

    let max = 0;
    for (const item of amChart.data) {
      for (const line of amChart.lines) {
        const value = item.values[line.key];
        if (value > max) {
          max = value;
        }
      }
    }
    return max;
  });

  /** Top Y-axis label value: the highest plotted value rounded up to the next 50. */
  yAxisTopValue = computed<number>(() => {
    const max = this.amChartMaxValue();
    return max > 0
      ? Math.ceil(max / this.Y_AXIS_STEP) * this.Y_AXIS_STEP
      : this.DEFAULT_Y_AXIS_TOP;
  });

  /** Middle Y-axis label value: the midpoint between the top and 0. */
  yAxisMiddleValue = computed<number>(() => this.yAxisTopValue() / 2);

  /**
   * Suffix on the Y-axis labels: the first series' unit, defaulting to '%'.
   * Spaced like the tooltip values — no space before '%', a space otherwise.
   */
  yAxisUnitSuffix = computed<string>(() => {
    const unit = this.data().amChart?.lines[0]?.unit ?? '%';
    return unit === '%' ? unit : ` ${unit}`;
  });

  isToggleMenuDropDownOpen = signal(false);

  hideTimes = input<boolean>(false);
  showIntervalSummary = input<boolean>(false);

  /**
   * Signal holding the current map of AmChart series.
   *
   * Maps series keys (string) to their corresponding am5xy.XYSeries instances.
   */
  amChartSeries = signal(new Map<string, am5xy.XYSeries[]>());

  amChartMouseOver = output<AmLineChartItem>();
  sizeChanged = output<number>();
  closed = output<void>();

  private root: am5.Root | undefined;
  readonly chartUuid = crypto.randomUUID();

  ToggleMenuCdkOverlayPositionPairs: ConnectionPositionPair[] = [
    {
      offsetX: 8,
      offsetY: -16,
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  private destroy$: Subject<void> = new Subject<void>();

  constructor() {
    effect(() => {
      const highlightedLegendItemInput = this.highlightedLegendItemInput();
      if (highlightedLegendItemInput) {
        this.onLegendItemMouseEnter(highlightedLegendItemInput);
      } else {
        this.onLegendItemMouseLeave();
      }
    });

    /**
     * Reactive effect that watches toggleMenu data and updates hiddenTimelineKeys accordingly.
     */
    effect(() => {
      const toggles = this.data().toggleMenu;
      this.updateHiddenTimelineKeys(toggles);
    });

    /**
     * Reactive effect that watches hiddenTimelineKeys and updates the amChart series' visibility
     */
    effect(() => {
      if (!this.data().amChart) return;

      this.updateAmChartSeriesVisibility(
        this.amChartSeries(),
        this.hiddenAmChartSeriesKeys()
      );
    });

    effect(() => {
      const componentHeight = this.getComponentHeight(
        this.displayedTimelines(),
        this.isAmChartDisplayed()
      );
      this.sizeChanged.emit(componentHeight);
    });

    effect(() => {
      const closestAmChartItemToCursor = this.closestAmChartItemToCursor();
      if (closestAmChartItemToCursor) {
        this.amChartMouseOver.emit(closestAmChartItemToCursor);
      }
    });

    // XYChart initial creation: afterNextRender runs only in the browser and only once after the initial render.
    // Both amCharts call sites used to sit inside `NgZone.runOutsideAngular` so the Animated theme's
    // tickers could not drive change detection. The app is zoneless — no timer or rAF drives CD at all —
    // so the escape has nothing to escape from and was removed with the `NgZone` injection.
    afterNextRender(() => {
      const data = this.data().amChart;
      if (data) {
        const rootAndSeriesMapPair = this.amChartService.createAmChart(
          data,
          this.startTime()!,
          this.endTime()!,
          this.chartUuid,
          this.amChartHeight(),
          this.yAxis() ? this.yAxisTopValue() : undefined
        );
        this.amChartSeries.set(rootAndSeriesMapPair.seriesMap);
        this.root = rootAndSeriesMapPair.root;
      }
    });

    // Tracking XYChart changes
    effect(() => {
      const data = this.data().amChart;

      if (data && this.root) {
        const root = this.root;
        const startTime = this.startTime();
        const endTime = this.endTime();
        const yAxisMax = this.yAxis() ? this.yAxisTopValue() : undefined;
        const seriesMapPair = this.amChartService.updateAmChart(
          root,
          data,
          startTime!,
          endTime!,
          yAxisMax
        );
        this.amChartSeries.set(seriesMapPair);
      }
    });
  }

  ngAfterViewInit() {
    this.updateTimelineContainerWidth();

    // Optionally: track resizes
    const resizeObserver = new ResizeObserver(() =>
      this.updateTimelineContainerWidth()
    );
    resizeObserver.observe(this.mainContainerRef.nativeElement);
  }

  /**
   * Clamps the first and last sections of a timeline to a global time range.
   *
   * This function ensures that:
   *  1. If `globalStart` is provided and the first section's `startTime` is earlier,
   *     it will be adjusted to match `globalStart`.
   *  2. If `globalEnd` is provided and the last section's `endTime` is later,
   *     it will be adjusted to match `globalEnd`.
   *
   * The function does not mutate the original `timeline` object.
   * It returns a new `TimelineChart` with updated `timelineSections`.
   *
   * @param timeline - The timeline to adjust, containing an array of `timelineSections`.
   * @param globalStart - The earliest allowed start time (inclusive). If `null` or `undefined`, no lower bound is applied.
   * @param globalEnd - The latest allowed end time (inclusive). If `null` or `undefined`, no upper bound is applied.
   * @returns A new `TimelineChart` with section start/end times clamped to the provided range.
   */
  clampTimelineSectionsToGlobalRange(
    timeline: TimelineChart,
    globalStart: Date | null | undefined,
    globalEnd: Date | null | undefined
  ): TimelineChart {
    if (!timeline.timelineSections?.length) {
      return { ...timeline, timelineSections: [] };
    }

    const sections = [...timeline.timelineSections];

    // Clamp first section's startTime
    if (globalStart) {
      const firstStart = new Date(sections[0].startTime!);
      if (firstStart < globalStart) {
        sections[0] = {
          ...sections[0],
          startTime: globalStart.toISOString(),
        };
      }
    }

    // Clamp last section's endTime
    if (globalEnd) {
      const lastIdx = sections.length - 1;
      const lastEnd = new Date(sections[lastIdx].endTime!);
      if (lastEnd > globalEnd) {
        sections[lastIdx] = {
          ...sections[lastIdx],
          endTime: globalEnd.toISOString(),
        };
      }
    }

    return { ...timeline, timelineSections: sections };
  }

  /**
   * Updates the hiddenTimelineKeys and hiddenAmChartSeriesKeys signals
   * based on the toggle menu data.
   *
   * @param toggles - Array of TimelineChartToggle objects from the toggle menu.
   */
  updateHiddenTimelineKeys(toggles: TimelineChartToggle[] | undefined): void {
    if (!toggles || toggles.length === 0) {
      this.hiddenTimelineKeys.set([]);
      this.hiddenAmChartSeriesKeys.set([]);
      return;
    }

    const hiddenTimelineKeys = toggles
      .filter((toggle) => !toggle.isEnabled && !toggle.isInsideAmChart)
      .map((toggle) => toggle.key);

    const hiddenAmChartKeys = toggles
      .filter((toggle) => !toggle.isEnabled && toggle.isInsideAmChart)
      .map((toggle) => toggle.key);

    this.hiddenTimelineKeys.set(hiddenTimelineKeys);
    this.hiddenAmChartSeriesKeys.set(hiddenAmChartKeys);
  }

  /**
   * Updates visibility and states of AmChart series based on hidden keys.
   *
   * @param seriesMap - Map of series keyed by their IDs.
   * @param hiddenKeys - Array of keys that should be hidden.
   */
  updateAmChartSeriesVisibility(
    seriesMap: Map<string, am5xy.XYSeries[]>,
    hiddenKeys: string[]
  ): void {
    seriesMap.forEach((seriesArray, key) => {
      if (hiddenKeys.includes(key)) {
        seriesArray.forEach((series: am5xy.XYSeries) => {
          series.states.applyAnimate('hidden');
        });
      } else {
        seriesArray.forEach((series: am5xy.XYSeries) => {
          series.show();
          series.states.applyAnimate('highlighted');
        });
      }
    });
  }

  /**
   * Validates an array of timeline chart sections for completeness, correct date formats,
   * logical ordering, and absence of overlaps.
   *
   * Performs the following checks for each section:
   * 1. Ensures `startTime` and `endTime` are present.
   * 2. Ensures both dates are valid.
   * 3. Ensures `startTime` is before `endTime`.
   * 4. Ensures the current section does not overlap with the next section.
   *
   * If any check fails, logs descriptive error messages to the console,
   * including the section's index and details.
   *
   * @param {TimelineChartSection[]} sections -
   *   The list of timeline chart sections to validate.
   *   Sections are expected to be sorted in ascending order by `startTime`.
   *
   * @returns {void} This function does not return anything.
   *   It only performs validations and logs errors to the console.
   */
  validateSection(sections: TimelineChartSection[]): void {
    const validateSingleSection = (
      section: TimelineChartSection,
      index: number
    ): { start: number; end: number } | null => {
      if (!section.startTime || !section.endTime) {
        console.error(
          `Missing startTime or endTime in section at index ${index}`,
          section
        );
        return null;
      }

      const start = this.parseDate(section.startTime);
      const end = this.parseDate(section.endTime);

      if (!this.isValidTime(start) || !this.isValidTime(end)) {
        console.error(
          `Invalid date format in section at index ${index}`,
          section
        );
        return null;
      }

      if (start >= end) {
        console.error(
          `startTime must be before endTime in section at index ${index}`,
          section
        );
      }

      return { start, end };
    };

    for (let i = 0; i < sections.length; i++) {
      const currentTimes = validateSingleSection(sections[i], i);
      if (!currentTimes) continue;

      if (i < sections.length - 1) {
        const nextTimes = validateSingleSection(sections[i + 1], i + 1);
        if (!nextTimes) continue;

        if (currentTimes.end > nextTimes.start) {
          console.error(
            `Overlap detected between section at index ${i} and ${i + 1}:\n` +
              `Section ${i} ends at ${sections[i].endTime}, but section ${i + 1} starts at ${sections[i + 1].startTime}`,
            sections[i],
            sections[i + 1]
          );
        }
      }
    }
  }

  parseDate(date: string | Date): number {
    return new Date(date).getTime();
  }

  isValidTime(value: number): boolean {
    return !isNaN(value);
  }

  hasValidDates(section: TimelineChartSection): boolean {
    return !!section.startTime && !!section.endTime;
  }

  /**
   * Fills time gaps between consecutive timeline sections by inserting
   * automatically generated placeholder sections, based on the `generateFillSections` option.
   *
   * A gap is defined as the time difference between the end of one section
   * and the start of the next (or between the provided start/end boundaries
   * and the first/last section) that exceeds the specified `gap` threshold.
   *
   * If `generateFillSections` is:
   *   - `false` → returns original sections without modification.
   *   - `true` → uses the provided `globalStart` and `globalEnd` for gap detection/filling.
   *   - `{ startTime?: string; endTime?: string }` → uses the provided values, falling back to `globalStart` / `globalEnd`.
   *
   * @param {TimelineChartSection[]} timelineSections
   *   The list of timeline sections, assumed to be sorted by start time.
   * @param {boolean | { startTime?: string; endTime?: string }} generateFillSections
   *   Controls whether and how to fill gaps.
   * @param {Date} globalStart
   *   The global start time for the timeline.
   * @param {Date} globalEnd
   *   The global end time for the timeline.
   * @param {number} [gap=this.DEFAULT_TIMELINE_GAP_TO_FILL]
   *   The minimum gap duration (in milliseconds) that will trigger insertion of a generated section.
   * @returns {TimelineChartSection[]}
   *   A new array of timeline sections with generated placeholder sections if filling is enabled.
   */
  fillTimelineGaps(
    timelineSections: TimelineChartSection[],
    generateFillSections: FillSectionGeneration | undefined,
    globalStart: Date,
    globalEnd: Date,
    gap: number = this.DEFAULT_TIMELINE_GAP_TO_FILL
  ): TimelineChartSection[] {
    // Skip filling entirely
    if (!generateFillSections) {
      return timelineSections;
    }

    // Determine effective start/end
    let startTime: Date;
    let endTime: Date;

    if (generateFillSections === true) {
      startTime = globalStart;
      endTime = globalEnd;
    } else {
      startTime = generateFillSections.startTime
        ? new Date(generateFillSections.startTime)
        : globalStart;
      endTime = generateFillSections.endTime
        ? new Date(generateFillSections.endTime)
        : globalEnd;
    }

    const result: TimelineChartSection[] = [];
    const generatedSectionTemplate = {
      color: this.GENERATED_SECTION_COLOR,
      key: this.GENERATED_SECTION_KEY,
    };

    const parseDate = (str?: string): number =>
      str ? new Date(str).getTime() : 0;

    const createSection = (
      start: number,
      end: number
    ): TimelineChartSection => ({
      ...generatedSectionTemplate,
      startTime: new Date(start).toISOString(),
      endTime: new Date(end).toISOString(),
      uuid: crypto.randomUUID(),
    });

    let previousEnd = startTime.getTime();

    for (const section of timelineSections) {
      const sectionStart = parseDate(section.startTime);
      const sectionEnd = parseDate(section.endTime);

      // Fill gap if needed
      if (sectionStart - previousEnd > gap) {
        result.push(createSection(previousEnd, sectionStart));
      }

      // Push original section
      result.push(section);

      previousEnd = sectionEnd;
    }

    // Fill gap after last section
    if (endTime.getTime() - previousEnd > gap) {
      result.push(createSection(previousEnd, endTime.getTime()));
    }

    return result;
  }

  /**
   * Checks if all sections in a timeline have valid start and end times.
   *
   * @param {TimelineChartSection[] | undefined} timeline
   *   The array of timeline sections to check.
   * @returns {boolean}
   *   `true` if every section has both `startTime` and `endTime` defined, otherwise `false`.
   */
  isTimelineConvertibleFromIntervalToRatio(
    timeline: TimelineChartSection[] | undefined
  ): boolean {
    if (!timeline) {
      console.error('Timeline chart section data is missing:', timeline);
      return false;
    }
    return timeline.every(
      (section) => Boolean(section.startTime) && Boolean(section.endTime)
    );
  }

  /**
   * Converts all timeline section intervals to ratios relative to the total timeline length.
   *
   * When the overall interval has no span — `startTime()` equals `endTime()`,
   * which is what a single-sample payload produces — there is nothing to divide
   * by, so the chart collapses to a single full-width section instead of giving
   * every section a `NaN%` width.
   *
   * @param {TimelineChart} timelineChart
   *   The timeline chart containing all sections.
   * @returns {TimelineChart}
   *   A new timeline chart with each section's `ratio` property calculated,
   *   or the original chart if conversion is not possible.
   */
  convertTimelineIntervalToRatio(timelineChart: TimelineChart): TimelineChart {
    const { timelineSections } = timelineChart;
    if (!this.isTimelineConvertibleFromIntervalToRatio(timelineSections)) {
      return timelineChart;
    }

    if (!this.sumIntervalInMs()) {
      return {
        ...timelineChart,
        timelineSections: timelineSections
          .slice(0, 1)
          .map((section) => ({ ...section, ratio: 100 })),
      };
    }

    return {
      ...timelineChart,
      timelineSections: timelineSections.map((section) =>
        this.convertTimelineSectionIntervalToRatio(section)
      ),
    };
  }

  /**
   * Converts a single timeline section's interval into a ratio of the total timeline length.
   *
   * @remarks
   * Assumes a non-zero overall interval; {@link convertTimelineIntervalToRatio}
   * is the only caller and handles the zero-span case before delegating here.
   *
   * @param {TimelineChartSection} section
   *   The section to convert.
   * @returns {TimelineChartSection}
   *   The updated section with a `ratio` property representing its percentage of the total.
   */
  convertTimelineSectionIntervalToRatio(
    section: TimelineChartSection
  ): TimelineChartSection {
    const totalDurationMs = this.sumIntervalInMs()!;
    const sectionDurationMs = this.getIntervalInMs(
      section.startTime!,
      section.endTime!
    );

    return {
      ...section,
      ratio: (sectionDurationMs / totalDurationMs) * 100,
    };
  }

  /**
   * Gets the total duration (in milliseconds) covered by all sections in a timeline.
   *
   * @param {TimelineChartSection[]} sections
   *   The list of timeline sections (must be ordered).
   * @returns {number}
   *   Duration from the start time of the first section to the end time of the last section.
   */
  getTimelineIntervalInMs(sections: TimelineChartSection[]): number {
    if (!sections.length) return 0;
    const firstStart = sections[0].startTime!;
    const lastEnd = sections[sections.length - 1].endTime!;
    return this.getIntervalInMs(firstStart, lastEnd);
  }

  /**
   * Calculates the duration (in milliseconds) between two dates or date strings.
   *
   * @param {string | Date} start
   *   The start date/time.
   * @param {string | Date} end
   *   The end date/time.
   * @returns {number}
   *   The difference between `end` and `start` in milliseconds.
   */
  getIntervalInMs(start: string | Date, end: string | Date): number {
    const startDate = start instanceof Date ? start : new Date(start);
    const endDate = end instanceof Date ? end : new Date(end);
    return endDate.getTime() - startDate.getTime();
  }

  /**
   * Calculates the CSS `calc()` string representing the relative position of a time
   * within the total timeline interval, adjusted by an offset in pixels.
   *
   * @param time - The target time as a Date object or ISO string.
   * @param offSetInPixels - The pixel offset to subtract from the calculated percentage.
   * @returns A CSS `calc()` string combining the percentage position and pixel offset.
   */
  getRelativePosition(time: Date | string, offSetInPixels: number): string {
    const startTime = this.startTime();
    const totalIntervalMs = this.sumIntervalInMs();

    return this.timelineChartService.getRelativePosition(
      time,
      offSetInPixels,
      startTime!,
      totalIntervalMs!
    );
  }

  /**
   * Applies a temporary highlight to the series with the specified key,
   * fading all other series.
   *
   * @param hoveredKey - The key of the series to highlight. If undefined, all series will be faded.
   */
  addTemporaryHighlight(hoveredKey?: string): void {
    this.amChartSeries().forEach(
      (seriesArray: am5xy.XYSeries[], key: string) => {
        seriesArray.forEach((series: am5xy.XYSeries) => {
          series.states.applyAnimate(
            key === hoveredKey ? 'highlighted' : 'faded'
          );
        });
      }
    );
  }

  fadeAllAmChartSeries(): void {
    this.amChartSeries().forEach((seriesArray: am5xy.XYSeries[]) => {
      seriesArray.forEach((series: am5xy.XYSeries) => {
        series.states.applyAnimate('faded');
      });
    });
  }

  /**
   * Removes the fade effect from all series by applying the 'highlighted' state,
   * effectively restoring full opacity to all.
   */
  removeAllFaded(): void {
    this.amChartSeries().forEach((seriesArray: am5xy.XYSeries[]) => {
      seriesArray.forEach((series: am5xy.XYSeries) => {
        series.states.applyAnimate('highlighted');
      });
    });
  }

  /**
   * Calculates the CSS width value for a timeline section based on its ratio and gap.
   *
   * @param ratio - The width ratio as a percentage (0-100).
   * @param gap - The total gap in pixels between sections (default is DEFAULT_TIMELINE_GAP).
   * @returns A CSS `calc()` string that subtracts half the gap from the percentage width.
   */
  getSectionWidthCssValue(
    ratio: number,
    gap: number = this.DEFAULT_TIMELINE_GAP
  ): string {
    return `calc(${ratio}% - ${gap / 2}px)`;
  }

  /**
   * Determines if a timeline section should be displayed as faded.
   *
   * A section is faded if:
   * - There is a highlighted legend item,
   * - AND the sectionKey is not the highlighted one,
   * - AND the sectionKey is not the special generated section key.
   *
   * @param sectionKey - The key of the timeline section to check.
   * @returns True if the section should be faded, false otherwise.
   */
  isFaded(sectionKey: string | undefined) {
    const highlightedLegendItem = this.highlightedLegendItem();
    const highlightedKey =
      highlightedLegendItem && isTimelineLegendItem(highlightedLegendItem)
        ? highlightedLegendItem.sectionKey
        : null;

    const isNoHighlight = !this.highlightedLegendItem();
    const isHighlighted = highlightedKey === sectionKey;
    const isGenerated = sectionKey === this.GENERATED_SECTION_KEY;

    return !(isNoHighlight || isHighlighted || isGenerated);
  }

  /**
   * Handles mouse move events over the timeline container element.
   *
   * Calculates the horizontal mouse position relative to the timeline container,
   * determining the offset in pixels, the container width, and the corresponding
   * percentage position (clamped between 0 and 100).
   *
   * Updates the `cursorPositionOnTimeline` signal with these calculated values.
   *
   * @param {MouseEvent} event - The mouse move event triggered by user interaction.
   */
  onTimelineMouseMove(event: MouseEvent): void {
    const timelineContainerElement: HTMLElement =
      this.timelineContainerRef.nativeElement;
    const rect = timelineContainerElement.getBoundingClientRect();

    const offsetX = event.clientX - rect.left;
    const timelineWidth = rect.width;
    const percentage = Math.max(
      0,
      Math.min(100, (offsetX / timelineWidth) * 100)
    );

    this.cursorPositionOnTimeline.set({
      offsetX,
      timelineWidth,
      percentage,
    });
  }

  /**
   * Handles mouse move events over the AmChart container element.
   *
   * Similar to `onTimelineMouseMove`, this function calculates the horizontal
   * mouse position relative to the AmChart container and updates the
   * `cursorPositionOnTimeline` signal accordingly.
   *
   * @param {MouseEvent} event - The mouse move event triggered by user interaction.
   */
  onAmChartMouseMove(event: MouseEvent): void {
    const amChartContainerElement: HTMLElement =
      this.amChartContainerRef.nativeElement;
    const rect = amChartContainerElement.getBoundingClientRect();

    const offsetX = event.clientX - rect.left;
    const timelineWidth = rect.width;
    const percentage = Math.max(
      0,
      Math.min(100, (offsetX / timelineWidth) * 100)
    );

    this.cursorPositionOnTimeline.set({
      offsetX,
      timelineWidth,
      percentage,
    });
  }

  /**
   * Calculates the vertical margin between timeline sections based on their heights.
   *
   * - Returns 0 if the current section is the last one.
   * - Uses a larger margin if either the current or the next section's height exceeds 10.
   * - Otherwise, uses a smaller margin.
   *
   * @param index - The index of the current timeline section.
   * @param isLast - Whether the current section is the last in the list.
   * @returns The margin size in pixels.
   */
  getTimelineMargin(index: number, isLast: boolean): number {
    if (isLast) {
      return 0;
    }

    const timelines = this.timelines();
    const currentHeight =
      timelines[index]?.height ?? this.DEFAULT_SECTION_HEIGHT;
    const nextHeight =
      timelines[index + 1]?.height ?? this.DEFAULT_SECTION_HEIGHT;

    return currentHeight > 10 || nextHeight > 10
      ? this.TIMELINE_MARGIN_LARGE
      : this.TIMELINE_MARGIN_SMALL;
  }

  /**
   * Toggles the visibility of a timeline by updating the hidden timeline keys.
   *
   * - If the timeline key is currently hidden, it will be removed from the hidden list.
   * - If it is currently visible, it will be added to the hidden list.
   *
   * @param toggle - TimelineChartToggle that has been clicked
   */
  onTimelineChartToggleClick(toggle: TimelineChartToggle): void {
    if (!toggle.key) return;
    this.toggleHidden(toggle.key, !!toggle.isInsideAmChart);
  }

  onLegendItemClick(legendItem: TimelineChartLegendItem): void {
    if (!this.isLegendItemHidden(legendItem)) return;
    if (isTimelineLegendItem(legendItem) && legendItem.timelineKey) {
      this.toggleHidden(legendItem.timelineKey, false);
    }
    if (isAmChartLegendItem(legendItem)) {
      this.toggleHidden(legendItem.seriesKey, true);
    }
  }

  toggleHidden(key: string, isInAmchart: boolean): void {
    const arraySignalToModify = isInAmchart
      ? this.hiddenAmChartSeriesKeys
      : this.hiddenTimelineKeys;

    const current = arraySignalToModify();
    const updated = current.includes(key)
      ? current.filter((key: string) => key !== key)
      : [...current, key];

    arraySignalToModify.set(updated);
  }

  toggleMenuDropdown(): void {
    this.isToggleMenuDropDownOpen.update((value: boolean) => !value);
  }

  /**
   * Determines if a legend item is hidden based on the hidden timeline and series keys.
   *
   * @param item - The legend item to check.
   * @returns `true` if the item is hidden; otherwise, `false`.
   */
  isLegendItemHidden(item: TimelineChartLegendItem): boolean {
    return this.timelineChartService.isLegendItemHidden(
      item,
      this.hiddenTimelineKeys(),
      this.hiddenAmChartSeriesKeys()
    );
  }

  /**
   * Handles mouse enter event on a legend item.
   *
   * - Highlights the legend item if it is not hidden.
   * - Applies a temporary highlight effect on the corresponding series.
   *
   * @param legendItem - The legend item being hovered.
   */
  onLegendItemMouseEnter(legendItem: TimelineChartLegendItem): void {
    this.highlightedLegendItem.set(
      this.isLegendItemHidden(legendItem) ? null : legendItem
    );
    if (isAmChartLegendItem(legendItem)) {
      this.addTemporaryHighlight(legendItem.seriesKey);
    } else {
      this.fadeAllAmChartSeries();
    }
  }

  /**
   * Handles mouse leave event on a legend item.
   *
   * - Clears any highlighted legend item.
   * - Removes the temporary faded effect from all series.
   */
  onLegendItemMouseLeave(): void {
    this.highlightedLegendItem.set(null);
    this.removeAllFaded();
  }

  getComponentHeight(
    displayedTimelines: TimelineChart[],
    isAmChartDisplayed: boolean
  ): number {
    const timelinesHeight = this.getTimelinesHeight(displayedTimelines);
    const amChartHeight = isAmChartDisplayed ? this.amChartHeight() : 0;
    const gap = (isAmChartDisplayed ? 3 : 2) * this.MAIN_CONTAINER_GAP;
    const padding = 2 * 8;

    return (
      timelinesHeight +
      amChartHeight +
      this.TIME_LABEL_HEIGHT +
      this.LEGEND_HEIGHT +
      padding +
      gap
    );
  }

  getTimelinesHeight(displayedTimelines: TimelineChart[]): number {
    if (!displayedTimelines.length) return 0;

    let totalHeight = 0;

    for (let i = 0; i < displayedTimelines.length; i++) {
      const currentHeight =
        displayedTimelines[i].height ?? this.DEFAULT_SECTION_HEIGHT;
      const isLast = i === displayedTimelines.length - 1;
      const margin = this.getTimelineMargin(i, isLast);

      totalHeight += currentHeight + margin;
    }

    return totalHeight;
  }

  isToggleActive(toggle: TimelineChartToggle): boolean {
    const arrayToCheck = toggle.isInsideAmChart
      ? this.hiddenAmChartSeriesKeys()
      : this.hiddenTimelineKeys();
    return !arrayToCheck.includes(toggle.key);
  }

  updateTimelineContainerWidth(): void {
    if (this.mainContainerRef?.nativeElement) {
      this.timelineWidth.set(this.mainContainerRef.nativeElement.offsetWidth);
    }
  }

  /**
   * Removes sections from the timeline that would render smaller than the specified minimum width.
   *
   * @param timeline - The timeline object containing sections to filter.
   * @param minimumSectionWidth - The minimum allowed width (in pixels) for a section to remain visible.
   * @param fullWidth - The total pixel width of the timeline container.
   * @returns A new `TimelineChart` with only sections that meet the minimum width requirement.
   */
  removeSectionUnderMinWidth(
    timeline: TimelineChart,
    minimumSectionWidth: number,
    fullWidth: number
  ): TimelineChart {
    // Ensure ratios are calculated for each section
    const { timelineSections } = this.convertTimelineIntervalToRatio(timeline);

    const gap = timeline.gap ?? this.DEFAULT_TIMELINE_GAP;

    const filteredSections = timelineSections.filter(
      (section: TimelineChartSection) => {
        if (section.ratio == null) {
          return false;
        }

        const sectionWidth = (fullWidth * section.ratio) / 100 - gap / 2;
        return sectionWidth >= minimumSectionWidth;
      }
    );

    // Return timeline with cleaned-up sections (ratios reset)
    return {
      ...timeline,
      timelineSections: filteredSections.map((section) => ({
        ...section,
        ratio: undefined,
      })),
    };
  }

  attachUUID(section: TimelineChartSection): TimelineChartSection {
    const uuid = crypto.randomUUID();
    return {
      ...section,
      uuid,
    };
  }

  isIntervalMoreThanTwoDays(start: Date, end: Date): boolean {
    const msInDay = 24 * 60 * 60 * 1000; // milliseconds in a day
    const diffMs = end.getTime() - start.getTime();
    return diffMs > 2 * msInDay;
  }

  /**
   * Generates an array of Date objects representing each day in the interval
   * between `start` and `end`, inclusive. Each Date is set a specific hour
   *
   * @param {Date} start - The start date of the interval.
   * @param {Date} end - The end date of the interval.
   * @param {number} at - The hour which to be set
   * @returns {Date[]} An array of Date objects, one per day, all set to the given hour.
   */
  getDatesAt(start: Date, end: Date, at: number): Date[] {
    const dates: Date[] = [];
    const current = new Date(start);

    current.setHours(at, 0, 0, 0);

    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }

  /**
   * Determines whether a given date has **no data** in the `TimelineChartInputV2` object.
   *
   * The function inspects both:
   * - The `amChart.data` array (if present), checking whether any `AmLineChartItem`
   *   has a `date` that falls on the same calendar day as the provided `date` parameter.
   * - The `timelines` array, checking whether any `TimelineChartSection`
   *   has an interval (`startTime`–`endTime`) that includes the given date.
   *
   * A section is considered to contain the date if:
   * - Both `startTime` and `endTime` are present and `startTime ≤ date ≤ endTime`.
   * - Only `startTime` is present and `date ≥ startTime`.
   * - Only `endTime` is present and `date ≤ endTime`.
   *
   * The function returns `true` **only when neither**
   * `amChart.data` nor `timelineSections` contain data on that date.
   *
   * @param date The date to check for data presence.
   * @returns `true` if no data exists on the given date in either `amChart` or `timelineSections`; otherwise `false`.
   */
  isDayWithoutValue(date: Date): boolean {
    // normalize a Date to local YYYY-MM-DD (midnight) for calendar-day comparisons
    const normalizeDateOnly = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

    const targetDay = normalizeDateOnly(date).getTime();

    const parseToDate = (s?: string | undefined | null): Date | undefined => {
      if (!s) return undefined;
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return undefined;
      return d;
    };

    const sameCalendarDay = (a: Date, bDayTime: number) =>
      normalizeDateOnly(a).getTime() === bDayTime;

    // 1) Check amChart.data
    const amData = this.processedData().amChart?.data ?? [];
    const hasAmDataOnDay = amData.some((item) => {
      if (!item?.date) return false;
      const itemDate = parseToDate(item.date);
      if (!itemDate) return false;
      return sameCalendarDay(itemDate, targetDay);
    });

    if (hasAmDataOnDay) return false; // there IS data -> so function result should be false

    // 2) Check timelines' sections intervals
    const timelines = this.processedData().timelines ?? [];
    const sectionContainsDate = timelines.some((timeline) => {
      const sections = timeline.timelineSections ?? [];
      return sections.some((sec) => {
        const start = parseToDate(sec.startTime);
        const end = parseToDate(sec.endTime);

        // if both missing, we can't say it contains the date
        if (!start && !end) return false;

        const target = date.getTime();

        // if both present -> inclusive range check
        if (start && end) {
          return target >= start.getTime() && target <= end.getTime();
        }

        // only start present -> date must be >= start (inclusive)
        if (start && !end) {
          return target >= start.getTime();
        }

        // only end present -> date must be <= end (inclusive)
        if (!start && end) {
          return target <= end.getTime();
        }

        return false;
      });
    });

    if (sectionContainsDate) return false;

    // none found in amChart.data nor in any timeline sections
    return true;
  }

  isTimeLabelGray(date: Date): boolean {
    return this.daysWithoutValue().includes(date);
  }

  ngOnDestroy(): void {
    if (this.root) {
      this.root.dispose();
      this.root = undefined;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
